import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import CompanyAdminLayout from "../../layouts/CompanyAdminLayout";
import {
  getOrgStartedTeamScenarios,
  stopTeamScenario,
} from "../../api/services/labService";
import {
  FlaskConical,
  Clock,
  ArrowRight,
  Users,
  AlertCircle,
  CheckCircle,
  Square,
  Loader2,
} from "lucide-react";

// ─── helpers ──────────────────────────────────────────────────────────────────

function normalizeDifficulty(difficulty) {
  return (difficulty || "").trim().toLowerCase();
}

function getDifficultyStyle(difficulty) {
  const n = normalizeDifficulty(difficulty);
  if (n === "easy" || n === "low")
    return {
      pill: "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
      gauge: "low",
    };
  if (n === "medium" || n === "intermediate")
    return {
      pill: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
      gauge: "medium",
    };
  if (n === "hard" || n === "advanced" || n === "high")
    return {
      pill: "bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400",
      gauge: "high",
    };
  return {
    pill: "bg-(--color-bg-secondary) text-(--color-text-muted)",
    gauge: null,
  };
}

function normalizeObjectives(lo) {
  if (!lo) return null;
  if (Array.isArray(lo) && lo.length > 0) return lo;
  if (typeof lo === "string" && lo.trim()) return [lo.trim()];
  return null;
}

// ─── DifficultyGaugeIcon ──────────────────────────────────────────────────────

function DifficultyGaugeIcon({ difficulty, className = "" }) {
  const level = getDifficultyStyle(difficulty).gauge;

  if (!level) {
    return (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={className}
        fill="none"
      >
        <path
          d="M4 16a8 8 0 0 1 16 0"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.35"
        />
        <circle cx="12" cy="16" r="2" fill="currentColor" opacity="0.55" />
      </svg>
    );
  }

  const styles = {
    low: { arcColor: "#34d399", needle: "rotate(-35 12 16)" },
    medium: { arcColor: "#facc15", needle: "rotate(0 12 16)" },
    high: { arcColor: "#ef4444", needle: "rotate(35 12 16)" },
  };
  const { arcColor, needle } = styles[level];

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      fill="none"
    >
      <path
        d="M4 16a8 8 0 0 1 16 0"
        stroke="#d1d5db"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M4 16a8 8 0 0 1 16 0"
        stroke={arcColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="14 50"
      />
      <path
        d="M12 16L18 11"
        stroke="#9ca3af"
        strokeWidth="1.8"
        strokeLinecap="round"
        transform={needle}
      />
      <circle cx="12" cy="16" r="2.2" fill="#9ca3af" />
      <circle cx="12" cy="16" r="0.95" fill="#f8fafc" />
    </svg>
  );
}

// ─── SkeletonCard ─────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="relative w-full rounded-lg border border-(--color-border) qv-card qv-noise flex flex-col opacity-75">
      {/* ── Zone 1: Header ── */}
      <div className="flex items-start justify-between p-5 pb-0">
        <div className="h-9 w-9 rounded-xl bg-(--color-bg-secondary) animate-pulse" />
        <div className="h-3 w-8 rounded bg-(--color-bg-secondary) animate-pulse mt-1" />
      </div>

      {/* ── Zone 2: Title + description ── */}
      <div className="px-5 pt-3 space-y-2">
        <div className="h-5 w-2/3 rounded bg-(--color-bg-secondary) animate-pulse" />
        <div className="space-y-1.5 pt-1">
          <div className="h-3 w-full rounded bg-(--color-bg-secondary) animate-pulse" />
          <div className="h-3 w-5/6 rounded bg-(--color-bg-secondary) animate-pulse" />
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="mx-5 mt-4 border-t border-(--color-border)" />

      {/* ── Zone 3: Learning objectives ── */}
      <div className="px-5 pt-3 space-y-2">
        <div className="h-2.5 w-2/5 rounded bg-(--color-bg-secondary) animate-pulse" />
        <div className="h-3 w-4/5 rounded bg-(--color-bg-secondary) animate-pulse" />
      </div>

      {/* ── Divider ── */}
      <div className="mx-5 mt-4 border-t border-(--color-border)" />

      {/* ── Zone 4: Progress bar ── */}
      <div className="px-5 pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="h-2.5 w-1/3 rounded bg-(--color-bg-secondary) animate-pulse" />
          <div className="h-2.5 w-1/6 rounded bg-(--color-bg-secondary) animate-pulse" />
        </div>
        <div className="h-1.5 w-full rounded-full bg-(--color-bg-secondary) animate-pulse" />
      </div>

      {/* ── Divider ── */}
      <div className="mx-5 mt-4 border-t border-(--color-border)" />

      {/* ── Zone 5: Meta — 2x2 grid ── */}
      <div className="px-5 pt-3 pb-4 grid grid-cols-2 gap-2 sm:gap-x-4 sm:gap-y-3">
        <div className="h-3 w-20 rounded bg-(--color-bg-secondary) animate-pulse" />
        <div className="h-3 w-24 rounded bg-(--color-bg-secondary) animate-pulse" />
        <div className="h-3 w-16 rounded bg-(--color-bg-secondary) animate-pulse" />
        <div className="h-3 w-24 rounded bg-(--color-bg-secondary) animate-pulse" />
      </div>

      {/* ── Zone 6: CTA ── */}
      <div className="px-5 pb-5 mt-auto">
        <div className="h-10.5 w-full rounded-lg bg-(--color-bg-secondary) animate-pulse" />
      </div>
    </div>
  );
}

// ─── TeamScenarioCard ─────────────────────────────────────────────────────────

function TeamScenarioCard({ run, onStop }) {
  const navigate = useNavigate();
  const {
    scenario,
    scenario_id,
    team_id,
    started_by_username,
    expires_at,
    answered_count,
    total_questions,
    team_name,
  } = run;

  const [isStopping, setIsStopping] = useState(false);
  const [stopError, setStopError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const openConfirm = (e) => {
    e.stopPropagation();
    if (isStopping) return;
    setStopError("");
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (isStopping) return;
    setConfirmOpen(false);
  };

  const handleStop = async () => {
    if (isStopping) return;
    try {
      setIsStopping(true);
      setStopError("");
      await stopTeamScenario({ scenarioId: scenario_id, teamId: team_id });
      onStop?.(run.id);
    } catch (err) {
      setStopError(err?.message || "Failed to stop challenge.");
      setIsStopping(false);
      setConfirmOpen(false);
    }
  };

  // Close confirm modal on Escape + lock body scroll while open
  useEffect(() => {
    if (!confirmOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !isStopping) setConfirmOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [confirmOpen, isStopping]);

  // Live time calculation — recalculates every 60 s
  const calcMinutesLeft = useCallback(
    () => Math.max(0, Math.round((new Date(expires_at) - Date.now()) / 60000)),
    [expires_at],
  );
  const [minutesLeft, setMinutesLeft] = useState(calcMinutesLeft);
  useEffect(() => {
    const id = setInterval(() => setMinutesLeft(calcMinutesLeft()), 60000);
    return () => clearInterval(id);
  }, [calcMinutesLeft]);
  const hoursLeft = Math.floor(minutesLeft / 60);
  const minsLeft = minutesLeft % 60;
  const timeLeftLabel =
    hoursLeft > 0 ? `${hoursLeft}h ${minsLeft}m` : `${minsLeft}m`;
  const isExpiringSoon = minutesLeft < 120;
  const isExpired = minutesLeft === 0;
  if (isExpired) return null;

  const difficulty = scenario?.difficulty_level ?? scenario?.difficulty ?? null;
  const timeEstimate = scenario?.time_estimate ?? null;
  const { pill: difficultyPill } = getDifficultyStyle(difficulty);
  const objectives = normalizeObjectives(scenario?.learning_objectives);

  // Progress — team scenarios always have 10 questions.
  const total = total_questions ?? scenario?.total_questions ?? 10;
  const answered = answered_count ?? 0;
  const progressPct = total ? Math.round((answered / total) * 100) : 0;
  const isComplete = answered >= total && total > 0;

  // Card visual state
  const cardBase =
    "relative w-full text-left rounded-lg border flex flex-col transition-all";
  const cardStyle =
    isExpiringSoon && !isComplete
      ? `${cardBase} border-orange-300 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-700`
      : isComplete
        ? `${cardBase} border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-700`
        : `${cardBase} border-(--color-border) qv-card qv-noise hover:border-(--color-primary)/60`;

  // In TeamScenarioCard.handleNavigate (TeamChallenges.jsx)
  const handleNavigate = () =>
    navigate(`/team-lab/${scenario_id}`, {
      state: {
        title: scenario?.title,
        description: scenario?.description,
        difficulty,
        time_estimate: timeEstimate,
        learning_objectives: scenario?.learning_objectives,
        team_id, // ← add this (team_id is already destructured from run)
      },
    });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleNavigate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleNavigate();
        }
      }}
      className={`${cardStyle} cursor-pointer focus:outline-none focus:ring-2 focus:ring-(--color-primary)/40`}
    >
      {/* ── Zone 1: Header ── */}
      <div className="flex items-start justify-between p-5 pb-0">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl border shrink-0 ${
            isComplete
              ? "border-emerald-200 bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-900/40"
              : isExpiringSoon
                ? "border-orange-200 bg-orange-100 dark:border-orange-700 dark:bg-orange-900/40"
                : "border-(--color-primary)/30 bg-(--color-primary)/10"
          }`}
        >
          {isComplete ? (
            <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          ) : (
            <FlaskConical
              className={`h-4 w-4 ${isExpiringSoon ? "text-orange-500" : "text-(--color-primary)"}`}
            />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {isComplete && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400">
              All done
            </span>
          )}
          {isExpiringSoon && !isComplete && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 inline-flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Expiring soon
            </span>
          )}
          <button
            type="button"
            onClick={openConfirm}
            disabled={isStopping}
            title="Stop challenge for the whole team"
            aria-label="Stop challenge"
            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-sm border border-red-300 bg-red-50 text-red-600 text-[11px] font-semibold dark:border-red-800 dark:bg-red-950/40 dark:text-red-400 transition-all hover:bg-red-500 hover:border-red-500 hover:text-white dark:hover:bg-red-600 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-400/40"
          >
            {isStopping ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Stopping
              </>
            ) : (
              <>
                <Square className="h-2.5 w-2.5 fill-current" />
                Stop Challenge
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Zone 2: Title + description ── */}
      <div className="px-5 pt-3 space-y-1.5">
        <h3 className="text-lg font-semibold text-(--color-text-primary) line-clamp-3 leading-snug">
          {scenario?.title || "Untitled Scenario"}
        </h3>
        <p className="text-xs leading-relaxed text-(--color-text-muted) dark:text-gray-400 line-clamp-3">
          {scenario?.description || "No description available."}
        </p>
        {stopError && (
          <p className="text-[11px] font-medium text-red-500 inline-flex items-center gap-1 pt-0.5">
            <AlertCircle className="h-3 w-3 shrink-0" />
            {stopError}
          </p>
        )}
      </div>

      {/* ── Divider ── */}
      <div className="mx-5 mt-4 border-t border-(--color-border)" />

      {/* ── Zone 4: Team progress bar ── */}
      <div className="px-5 pt-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-(--color-text-muted)">
            Team progress
          </span>
          <span
            className={`text-[11px] font-semibold ${isComplete ? "text-emerald-600 dark:text-emerald-400" : "text-(--color-text-primary)"}`}
          >
            {answered} / {total}
            {isComplete ? " ✓" : ""}
          </span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-800/80">
          <div
            className={`h-full rounded-full transition-all ${
              isComplete
                ? "bg-emerald-500"
                : isExpiringSoon
                  ? "bg-orange-400"
                  : "bg-(--color-primary)"
            }`}
            style={{ width: total ? `${progressPct}%` : "0%" }}
          />
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="mx-5 mt-4 border-t border-(--color-border)" />

      {/* ── Zone 5: Meta — 2x2 grid ── */}
      {/* ── Zone 5: Meta — 2x2 grid ── */}
      <div className="px-5 pt-3 pb-4 grid grid-cols-2 gap-2 sm:gap-x-4 sm:gap-y-2">
        {/* Difficulty */}
        <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11px]">
          <span className="text-(--color-text-muted)">Difficulty:</span>
          {difficulty ? (
            <span
              className={`font-semibold px-2 py-0.5 rounded ${difficultyPill}`}
            >
              {difficulty}
            </span>
          ) : (
            <span className="text-(--color-text-secondary)">TBD</span>
          )}
        </div>

        {/* Estimated time */}
        <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] text-(--color-text-muted)">
          <span>For:</span>
          <span
            className="font-medium text-(--color-text-secondary) truncate min-w-0"
            title={team_name}
          >
            {team_name || "—"}
          </span>
        </div>

        {/* Started by */}
        <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-(--color-text-muted) min-w-0">
          <span className="shrink-0">Started by:</span>
          <span
            className="font-medium text-(--color-text-secondary) truncate min-w-0"
            title={started_by_username}
          >
            {started_by_username}
          </span>
        </div>

        {/* Time left */}
        <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px]">
          {isExpiringSoon && !isExpired && (
            <AlertCircle className="h-3 w-3 shrink-0 text-red-400 hidden sm:block" />
          )}
          <span className="text-(--color-text-muted)">Time Left:</span>
          <span
            className={`font-medium ${
              isExpired
                ? "text-red-500"
                : isExpiringSoon
                  ? "text-red-400"
                  : "text-(--color-text-secondary)"
            }`}
          >
            {isExpired ? "Expired" : timeLeftLabel}
          </span>
        </div>
      </div>

      {/* ── Zone 6: CTA ── */}
      <div className="px-5 pb-5 mt-auto">
        {isComplete ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate();
            }}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-sm font-semibold py-2.5 transition-all hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
          >
            View walkthrough
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : isExpiringSoon ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate();
            }}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-orange-400 bg-orange-500 text-white text-sm font-semibold py-2.5 transition-all hover:bg-orange-600"
          >
            View Box
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate();
            }}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-(--color-primary) bg-(--color-primary) text-(--color-text-on-primary) text-sm font-semibold py-2.5 transition-all hover:opacity-90"
          >
            View Box
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* ── Stop confirmation modal ── */}
      {confirmOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-1000 flex items-center justify-center p-4"
            onClick={(e) => {
              e.stopPropagation();
              closeConfirm();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Confirm stop challenge"
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Dialog */}
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-sm border border-(--color-border) bg-(--color-bg) shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/40">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-(--color-text-primary)">
                      Stop this challenge?
                    </h2>
                    <p className="mt-1 text-sm leading-relaxed text-(--color-text-muted)">
                      This ends{" "}
                      <span className="font-semibold text-(--color-text-secondary)">
                        {scenario?.title || "this challenge"}
                      </span>{" "}
                      for the whole team.
                    </p>
                    {stopError && (
                      <p className="mt-2 text-[12px] font-medium text-red-500 inline-flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        {stopError}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-(--color-border) px-6 py-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeConfirm();
                  }}
                  disabled={isStopping}
                  className="inline-flex items-center justify-center rounded-lg border border-(--color-border) bg-transparent px-4 py-2 text-sm font-semibold text-(--color-text-secondary) transition-all hover:bg-(--color-bg-secondary) disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-(--color-primary)/30"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStop();
                  }}
                  disabled={isStopping}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-500 bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-red-600 hover:border-red-600 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-400/40"
                >
                  {isStopping ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Stopping…
                    </>
                  ) : (
                    <>
                      <Square className="h-3 w-3 fill-current" />
                      Stop challenge
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

// ─── TeamChallenges (admin org-wide view) ───────────────────────────────────────

export default function TeamChallenges() {
  const [runs, setRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let isActive = true;
    const fetchRuns = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const data = await getOrgStartedTeamScenarios();
        if (isActive) {
          const now = new Date();
          const activeRuns = (data?.runs || []).filter(
            (run) => new Date(run.expires_at) > now,
          );
          setRuns(activeRuns);
        }
      } catch (err) {
        if (isActive)
          setLoadError(err?.message || "Failed to load team challenges.");
      } finally {
        if (isActive) setIsLoading(false);
      }
    };
    fetchRuns();
    return () => {
      isActive = false;
    };
  }, []);

  const handleStopRun = useCallback((runId) => {
    setRuns((prev) => prev.filter((r) => r.id !== runId));
  }, []);

  return (
    <CompanyAdminLayout>
      <div className="w-full max-w-362.5 mx-auto px-8 py-8 space-y-6 min-h-screen">
        {/* ── Page header ── */}
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-(--color-text-muted) mb-1">
            Team
          </p>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">
            Team Challenges
          </h1>
          <p className="text-xs mt-0.5 text-(--color-text-muted)">
            Active challenges across all teams in your organization. Monitor
            progress in real time.
          </p>
        </div>

        {/* ── Content ── */}
        {isLoading ? (
          /* Skeleton grid */
          <div>
            <div className="h-3 w-32 rounded bg-(--color-bg-secondary) animate-pulse mb-4" />
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 items-stretch">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
        ) : loadError ? (
          /* Error state */
          <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <p className="text-sm font-semibold text-(--color-text-primary)">
              Failed to load
            </p>
            <p className="text-xs text-(--color-text-muted) max-w-xs">
              {loadError}
            </p>
          </div>
        ) : runs.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-(--color-border) bg-(--color-bg-secondary)">
              <Users className="h-6 w-6 text-(--color-text-muted)" />
            </div>
            <div>
              <p className="text-base font-semibold text-(--color-text-primary)">
                No active challenges
              </p>
              <p className="text-sm text-(--color-text-muted) max-w-xs mt-1 leading-relaxed">
                Your organization hasn't started any challenge yet. Browse the
                box catalog to kick one off.
              </p>
            </div>
          </div>
        ) : (
          /* Card grid */
          <div>
            <p className="text-xs text-(--color-text-muted) font-medium mb-4">
              {runs.length} Active{" "}
              {runs.length === 1 ? "Challenge" : "Challenges"}
            </p>
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 items-stretch">
              {runs.map((run) => (
                <TeamScenarioCard
                  key={run.id}
                  run={run}
                  onStop={handleStopRun}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </CompanyAdminLayout>
  );
}
